import React from 'react';
import { X, Maximize2, PanelRightClose } from 'lucide-react';
import { useWhatsAppDrawerStore, type DrawerChannel } from '@/lib/stores/whatsappDrawerStore';
import { SessionSelector } from '@/components/whatsapp2/SessionSelector';
import type { WhatsAppSessionConfig } from '@/lib/hooks/useWhatsAppConfigs';
import { iconTip } from '@/components/messages/ChatTooltip';
import { ChannelSelector } from './ChannelSelector';

interface DrawerHeaderProps {
  configs: WhatsAppSessionConfig[];
  activeConfigId: string | null;
  onSelectSession: (configId: string) => void;
  onAddSession?: () => void;
  /** Canali da mostrare nel selettore (WhatsApp e Team dipendono da permessi e impostazioni). */
  channelsVisible?: Partial<Record<DrawerChannel, boolean>>;
  pendingByChannel?: Partial<Record<DrawerChannel, number>>;
  /** Spazio stretto: canali con la sola icona e titolo nascosto. */
  narrow?: boolean;
  /** Campanella degli avvisi per i nuovi messaggi. */
  notificationControl?: React.ReactNode;
}

export function DrawerHeader({ configs, activeConfigId, onSelectSession, onAddSession, channelsVisible, pendingByChannel, narrow = false, notificationControl }: DrawerHeaderProps) {
  const { mode, setMode, close, channel, setChannel } = useWhatsAppDrawerStore();

  return (
    <div className={`flex items-center justify-between gap-2 h-12 ${narrow ? 'px-2' : 'px-4'} border-b border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-white dark:bg-[#202020] flex-shrink-0`}>
      {/* Left: title + channel selector + session selector */}
      <div className={`flex items-center ${narrow ? 'gap-2' : 'gap-3'} min-w-0`}>
        {!narrow && <h2 className="text-sm font-semibold text-[#37352F] dark:text-white whitespace-nowrap">Messaggi</h2>}
        <ChannelSelector value={channel} onChange={setChannel} pending={pendingByChannel} visible={channelsVisible} iconsOnly={narrow} />
        {channel === 'whatsapp' && (
          <SessionSelector
            configs={configs}
            activeConfigId={activeConfigId}
            onSelect={onSelectSession}
            onAddSession={onAddSession}
          />
        )}
      </div>

      {/* Right: avvisi + mode switch + close */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {notificationControl}

        <button
          onClick={() => setMode(mode === 'full' ? 'compact' : 'full')}
          className="p-1.5 rounded-md hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 text-[#5F5E5B] dark:text-white/70 transition-colors"
          {...iconTip(mode === 'full' ? 'Riduci a pannello laterale' : 'Allarga a tutta la pagina', 'bottom')}
        >
          {mode === 'full' ? (
            <PanelRightClose className="h-4 w-4" />
          ) : (
            <Maximize2 className="h-4 w-4" />
          )}
        </button>

        <button
          onClick={close}
          className="p-1.5 rounded-md hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 text-[#5F5E5B] dark:text-white/70 transition-colors"
          {...iconTip('Chiudi i messaggi (Esc)', 'bottom')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
